// src/components/Navigation/Header.tsx

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, LogOut, Package, BookOpen, Users, User } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext.tsx';
import { useAuth } from '../../hooks/useAuth.ts';
import { BRAND_NAME_TM, TAGLINE } from '../../config/branding.ts';
import LazyImage from '../UI/LazyImage.tsx';

const Header: React.FC = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const { isAuthenticated, openAuthModal, signOut, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string): boolean => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navLinkClass = (path: string): string => {
    const active = isActive(path);
    const base = 'flex items-center space-x-2 font-medium transition-colors duration-200 border-b-2';
    let activeStyle: string;
    if (active) {
      activeStyle = darkMode ? 'text-orange-200 border-orange-500' : 'text-orange-600 border-orange-500';
    } else {
      activeStyle = darkMode ? 'text-orange-300 hover:text-orange-200 border-transparent' : 'text-slate-600 hover:text-orange-600 border-transparent';
    }
    return `${base} ${activeStyle}`;
  };

  const handleGetStarted = (): void => openAuthModal();

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <nav
      className={`sticky top-0 z-40 border-b transition-all duration-300 backdrop-blur-md ${
        darkMode ? 'bg-slate-900/90 border-orange-800/50' : 'bg-white/90 border-slate-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* --- Header Bar: Desktop & Mobile --- */}
        <div className="flex items-center justify-between h-16 lg:h-20 relative">
          {/* --- LEFT: Logo/Branding --- */}
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0 z-10">
            <LazyImage
              src="/icons/web-app-manifest-512x512.png"
              alt="Life Re:Scripted Logo"
              className="w-8 h-8 lg:w-10 lg:h-10 object-contain"
              placeholder={
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-orange-200 dark:bg-orange-800 rounded animate-pulse" />
              }
            />
            <div className="flex flex-col">
              <span
                className={`font-display text-lg lg:text-xl font-bold transition-colors ${
                  darkMode ? 'text-orange-100' : 'text-slate-800'
                }`}
              >
                {BRAND_NAME_TM}
              </span>
              <span
                className={`block text-xs sm:text-sm lg:text-base font-medium transition-colors ${
                  darkMode ? 'text-orange-300/80' : 'text-slate-600'
                }`}
              >
                {TAGLINE}
              </span>
            </div>
          </Link>

          {/* --- CENTER: Nav links (absolutely centered, desktop only) --- */}
          <div className="hidden lg:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 space-x-8 xl:space-x-10 z-0">
            <Link to="/journal" className={`${navLinkClass('/journal')} text-base lg:text-xl pb-1`}>
              <BookOpen className="w-4 h-4 lg:w-6 lg:h-6" />
              <span>Journal</span>
            </Link>
            <Link to="/forge" className={`${navLinkClass('/forge')} text-base lg:text-xl whitespace-nowrap pb-1`}>
              <Package className="w-4 h-4 lg:w-6 lg:h-6" />
              <span>The Forge</span>
            </Link>
            <Link to="/community" className={`${navLinkClass('/community')} text-base lg:text-xl whitespace-nowrap pb-1`}>
              <Users className="w-4 h-4 lg:w-6 lg:h-6" />
              <span>Community</span>
            </Link>
          </div>

          {/* --- RIGHT: Stack name above controls, top-right --- */}
          <div className="flex flex-col items-end z-10">
            {/* Display name - on its own row, right-aligned */}
            {isAuthenticated && profile && (
              <span
                className={`text-xs font-medium mb-0.5 pr-1 ${darkMode ? 'text-orange-200' : 'text-slate-700'}`}
                style={{ minHeight: '20px' }} // Prevents header shift
              >
                Welcome, {profile.display_name}!
              </span>
            )}

            {/* Controls row - theme/profile/signout or get started */}
            <div className="flex items-center space-x-1">
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  darkMode
                    ? 'text-orange-300 hover:bg-slate-700'
                    : 'text-slate-600 hover:bg-orange-50'
                }`}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                      darkMode
                        ? 'text-orange-300 hover:text-orange-200'
                        : 'text-slate-600 hover:text-orange-600'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                      darkMode
                        ? 'text-orange-300 hover:text-orange-200'
                        : 'text-slate-600 hover:text-orange-600'
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:from-orange-700 hover:to-red-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- MOBILE: Nav links (always in a row, centered, flex-wrap if forced) --- */}
        <div className="flex flex-row flex-nowrap items-center justify-center gap-x-4 py-3 lg:hidden transition-all duration-300">
          <Link to="/journal" className={`${navLinkClass('/journal')} text-base sm:text-lg pb-1`}>
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Journal</span>
          </Link>
          <Link to="/forge" className={`${navLinkClass('/forge')} text-base sm:text-lg whitespace-nowrap pb-1`}>
            <Package className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>The Forge</span>
          </Link>
          <Link to="/community" className={`${navLinkClass('/community')} text-base sm:text-lg whitespace-nowrap pb-1`}>
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Community</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Header;
